/**
 * Resolvers for content that has a real per-language translation for some
 * languages (currently Urdu and Malay) but not others — module/program
 * names, module intro paragraphs, and Module 9 passage names. These live as
 * plain i18n keys (`module.<code>.name`, `module.<code>.intro`,
 * `program.<id>.name`, `passage.<code>.name`) rather than a database field,
 * deliberately: no backend/schema change, reusing the same static-JSON
 * mechanism as every other translated string in the app.
 *
 * `t()` (I18nContext) falls back to the English dictionary and, failing
 * that, returns the raw key string itself — so a key that was never
 * translated for the active language AND doesn't exist in en.json (true
 * here, since these keys only exist in ur.json/ms.json so far) comes back
 * as literally "module.foo.name". Each resolver below detects that exact
 * case and falls back to the module's own field instead, which is exactly
 * today's un-translated behavior (name_ar / the English intro string) for
 * every language other than Urdu and Malay.
 */

export function resolveModuleName(t, m) {
  if (!m) return "";
  const key = `module.${m.code}.name`;
  const val = t(key);
  return val === key ? m.name : val;
}

export function resolveModuleIntro(t, m) {
  if (!m) return "";
  const key = `module.${m.code}.intro`;
  const val = t(key);
  return val === key ? m.intro : val;
}

export function resolveProgramName(t, p) {
  if (!p) return "";
  const code = p.code || p.id;
  const key = `program.${code}.name`;
  const val = t(key);
  return val === key ? (p.name || p.title) : val;
}

export function resolveProgramTagline(t, p) {
  if (!p) return "";
  const code = p.code || p.id;
  const key = `program.${code}.tagline`;
  const val = t(key);
  return val === key ? p.tagline : val;
}

export function resolveProgramCategory(t, p) {
  if (!p) return "";
  const code = p.code || p.id;
  const key = `program.${code}.category`;
  const val = t(key);
  return val === key ? p.category : val;
}

export function resolvePassageName(t, passage) {
  if (!passage) return "";
  const key = `passage.${passage.code}.name`;
  const val = t(key);
  return val === key ? passage.name : val;
}

export function resolveModuleSubtitle(_t, m) {
  return m?.name_ar ?? "";
}

export function resolveAchievementTitle(t, a) {
  if (!a) return "";
  const key = `achievement.${a.id}.title`;
  const val = t(key);
  return val === key ? a.title : val;
}

export function resolveAchievementDesc(t, a) {
  if (!a) return "";
  const key = `achievement.${a.id}.desc`;
  const val = t(key);
  return val === key ? a.desc : val;
}
